import {makeAutoObservable, runInAction} from 'mobx'
import {logger} from 'yougile-util'
import {Tracking, TrackingEvent} from '@common/tracking'
import {MessageBox} from '@components/message-box'
import {fetchUnsplash} from '../api/fetch-unsplash'
import {handleErrorMsg} from '../handle-error-msg'
import {UnsplashBasic, UnsplashColor} from './types'

interface State {
  search: string
  unsplashColor: UnsplashColor
  currentPage: number
  totalPages: number
  waitFirstPage: boolean
  wait: boolean
  error?: string
  mode: 'own' | 'search'
}

export class UnsplashStore {
  state: State = {
    search: '',
    unsplashColor: 'none',
    currentPage: 0,
    totalPages: 0,
    waitFirstPage: false,
    wait: false,
    mode: 'own'
  }
  results: UnsplashBasic[] = []

  prevSearch: string
  prevColor: string
  searchEdited = false

  constructor() {
    makeAutoObservable(this, {}, {autoBind: true})
  }

  typeFrom: 'board' | 'profile' = 'profile'

  public resetSearch(): void {
    this.setSearch('')
  }

  private resetPages(): void {
    this.state.currentPage = 0
    this.state.totalPages = 1
    this.state.error = ''
    this.state.waitFirstPage = false
  }

  public setSearch(val: string, typeFrom?: 'board' | 'profile'): void {
    if (typeFrom) {
      this.typeFrom = typeFrom
    }
    if (this.state.search !== val) {
      this.state.search = val
      this.searchEdited = true
    }

    if (!val) {
      this.state.mode = 'own'
    }

    this.resetPages()
  }

  public searchWithColor(unsplashColor: UnsplashColor): void {
    if (unsplashColor === this.state.unsplashColor) {
      return
    }

    this.state.unsplashColor = unsplashColor
    this.searchEdited = true

    this.doSearch()
  }

  public get isSearch(): boolean {
    return this.state.mode === 'search'
  }

  public doSearch(): void {
    if (!this.state.search) {
      return
    }

    if (
      this.prevSearch === this.state.search &&
      this.prevColor === this.state.unsplashColor &&
      !this.searchEdited
    ) {
      return
    }

    this.prevSearch = this.state.search
    this.prevColor = this.state.unsplashColor
    this.searchEdited = false

    this.resetPages()

    this.state.waitFirstPage = true
    this.results = []

    void this.requestNextPage()
  }

  public setWait(val: boolean): void {
    this.state.wait = val
  }

  public async requestNextPage(): Promise<void> {
    if (!this.state.search) {
      return
    }

    this.state.mode = 'search'

    const nextPage = this.state.currentPage + 1
    if (nextPage > this.state.totalPages) {
      return
    }

    const query = this.state.search
    const color = this.state.unsplashColor

    try {
      const res = await fetchUnsplash(query, nextPage, color)
      if (
        res.query !== this.state.search ||
        color !== this.state.unsplashColor
      ) {
        return
      }

      runInAction(() => {
        this.state.totalPages = Number(res.totalPages)
        this.results = [...this.results, ...res.data]

        this.state.currentPage = nextPage
      })
    } catch (e: unknown) {
      runInAction(() => {
        logger.error('Cannot get photo from unsplash', e)
        if (e instanceof Error) {
          this.state.error = e?.message
          MessageBox.warn(handleErrorMsg(e.message))

          this.processError(e)
        } else {
          logger.error('Unhandled error', e)
        }
      })
    }

    runInAction(() => {
      this.state.waitFirstPage = false
      this.state.wait = false
    })
  }

  private processError(e: Error): void {
    const unsplashErrorRequestLimit = e.message?.includes('429')
    if (unsplashErrorRequestLimit) {
      if (this.typeFrom === 'profile') {
        void Tracking.send(TrackingEvent.profileUnsplashReachLimitPerUser)
      } else {
        void Tracking.send(TrackingEvent.boardSettingsUnsplashReachLimitPerUser)
      }
    } else {
      if (this.typeFrom === 'profile') {
        void Tracking.send(TrackingEvent.profileUnsplashHasAnotherError)
      } else {
        void Tracking.send(TrackingEvent.boardSettingsUnsplashHasAnotherError)
      }
    }
  }
}

export const unsplashStore = new UnsplashStore()
